import { Extension } from "@tiptap/core"
import Suggestion from "@tiptap/suggestion"
import tippy, { Instance as TippyInstance } from "tippy.js"
import "tippy.js/dist/tippy.css"
import { Editor } from "@tiptap/react"
import { Range } from "@tiptap/core"
import { EngineQueryResult, EngineOutputItem } from "types/engineOutput.types"

// Define the structure for command definitions
interface CommandDefinition {
  params: string[]
  description: string
  types?: string[]
}

// Define the type for fallback commands object
type FallbackCommands = Record<string, CommandDefinition>

// Define the structure for suggestion items
interface SuggestionItem {
  title: string
  params: string[]
  description: string
  types?: string[]
  loading: boolean
}

// Define the structure for command cache
interface CommandCache {
  list: string[] | null
  details: Record<string, CommandDefinition>
  pendingRequests: Record<string, Promise<CommandDefinition>>
}

// Define the structure for suggestion props
interface SuggestionProps {
  editor: Editor
  range: Range
  items: SuggestionItem[]
  clientRect?: (() => DOMRect) | null
}

// Define the structure for keyboard event props
interface KeyboardEventProps {
  event: KeyboardEvent
}

// Define the structure for suggestion command props
interface SuggestionCommandProps {
  editor: Editor
  range: Range
  props: { title: string }
}

// Define the structure for suggestion items props
interface SuggestionItemsProps {
  query: string
}

export const fallbackCommands: FallbackCommands = {
  PDUMP: {
    params: [],
    description:
      "Outputs a snapshot of the project's current state, including respondents, variables, log status, and project size.",
  },
  DUMP: {
    params: ["<name>"],
    description:
      "Outputs the entire data structure or specific data in JSON format.",
  },
  SDUMP: {
    params: ["<name>"],
    description: "Outputs a summarized JSON of the data structure.",
  },
  INFO: {
    params: ["<name>"],
    description:
      "Provides detailed information about the specified name or subname.",
  },
  OUTPUT: {
    params: ["<name>"],
    description: "Outputs the variable as JSON or Table format for XTAB/DTAB.",
  },
  OUTPUTJ: {
    params: ["<name>"],
    description: "Similar to OUTPUT but presents data in JSON format.",
  },
  XTAB: {
    params: ["<name>", "<var1>", "<var2>"],
    description: "Creates a crosstab of two variables.",
  },
  DTAB: {
    params: ["<name>", "<var1>", "<var2>"],
    description: "Generates a detailed data table for the specified variables.",
  },
  QTABLE: {
    params: ["<name>"],
    description:
      "Creates a root or nested QTable. QTables act as containers within the tree structure.",
  },
  FIELD: {
    params: ["<QTable>::<Field>", "<Type>"],
    description:
      "Creates a new field with a specified type and value under a QTable. Fields hold actual data and represent leaves of the tree.",
    types: ["INT", "REAL", "TEXT", "BOOL"],
  },
  REMOVE: {
    params: ["<name>"],
    description: "Removes a QTable or a field from a QTable.",
  },
  COPY: {
    params: ["<to>", "<from>"],
    description: "Copies data from one QTable or field to another.",
  },
  COPYSTRUCT: {
    params: ["<TargetQTable>", "<SourceQTable>"],
    description:
      "Copies the structure (without data) from a source QTable to a target QTable.",
  },
  APPEND: {
    params: ["<target_QTable>", "<source_field_or_QTable>"],
    description: "Adds content from one QTable/field to another.",
  },
  RENAME: {
    params: ["<QTABLE_NAME>", "<NEW_LABEL>"],
    description: "Renames a QTable or field.",
  },
  NOT: {
    params: ["<FIELD>"],
    description:
      "Reverses the values of a boolean field (false -> true, true -> false).",
  },
  BLANKTRUE: {
    params: ["<FIELD>"],
    description: "Changes Blank/Null values to true.",
  },
  BLANKFALSE: {
    params: ["<FIELD>"],
    description: "Changes Blank/Null values to false.",
  },
  BLANKZERO: {
    params: ["<FIELD>"],
    description: "Changes Blank/Null values to 0 for INT and REAL fields.",
  },
  SETBLANK: {
    params: ["<FIELD>"],
    description: "Changes all values to null.",
  },
  ASSIGN: {
    params: ["<target>", "<source>"],
    description:
      "Assigns values from one field/QTable to another or assigns a literal value. Supported types: INT, BOOL, REAL, TEXT.",
  },
  ADD: {
    params: ["<target>", "<source>"],
    description:
      "Adds values from one field/QTable to another. Supported types: INT, REAL.",
  },
  SUBTRACT: {
    params: ["<target>", "<source>"],
    description:
      "Subtracts values from one field/QTable from another. Supported types: INT, REAL.",
  },
  MULT: {
    params: ["<target>", "<source>"],
    description:
      "Multiplies values from one field/QTable with another. Supported types: INT, REAL.",
  },
  DIV: {
    params: ["<target>", "<source>"],
    description:
      "Divides values from one field/QTable by another. Supported types: INT, REAL.",
  },
  AND: {
    params: ["<target>", "<source>"],
    description:
      "Performs a logical AND operation on boolean fields. Supported type: BOOL.",
  },
  OR: {
    params: ["<target>", "<source>"],
    description:
      "Performs a logical OR operation on boolean fields. Supported type: BOOL.",
  },
  CODEBOOK: {
    params: [],
    description:
      "Provides a concise summary of headers/metadata without displaying actual data.",
  },
  RESET: {
    params: [],
    description: "Resets the dataset to its original state.",
  },
  CLEAR: {
    params: [],
    description: "Clears the entire dataset, deleting all QTables and fields.",
  },
  STOP: {
    params: [],
    description: "Terminates the execution of a script.",
  },
  FIND: {
    params: ["[search_string]"],
    description:
      "Searches for strings within the labels of fields/QTables. With EXTENDED keyword, also searches additional info.",
  },
  FINDR: {
    params: ["[regular_expression]"],
    description:
      "Searches for a regex pattern within the labels of fields/QTables. With EXTENDED keyword, also searches additional info.",
  },
  SCOPE: {
    params: ["<QTABLE>"],
    description: "Redefines the root node for subsequent operations.",
  },
  PROJECT: {
    params: ["<JSON>"],
    description: "Creates a new project using the specified JSON structure.",
  },
  REMEMBER: {
    params: ["<QTABLE>"],
    description: "Saves a QTable's state to recall later using RECALL.",
  },
  RECALL: {
    params: ["<QTABLE>"],
    description:
      "Recalls a QTable's state, allowing a revert to the original state when REMEMBER was called.",
  },
  FORGET: {
    params: ["<QTABLE>"],
    description: "Forgets the snapshot created using REMEMBER.",
  },
  ALIAS: {
    params: ["<alias_name>", "<QTable::Field>"],
    description: "Creates an alias for a QTable or field.",
  },
  FFIELD: {
    params: ["<FieldName>", "<QTableName>"],
    description:
      "Creates a boolean field based on conditions. Useful for filtering data across multiple QTables.",
  },
  USEWEIGHTS: {
    params: ["<WeightsColumn>"],
    description: "Applies weighting to data using the specified column.",
  },
  CALCWEIGHTS: {
    params: ["<FIELD>", "<weighting_factor>"],
    description: "Calculates and applies weighting factors to the data.",
  },
  RESPID: {
    params: [],
    description: "Command Information not available.",
  },
  APPENDSTRUCT: {
    params: ["<target_QTable>", "<source_QTable>"],
    description:
      "Appends the structure of one QTable to another, merging their fields.",
  },
  EXCL: {
    params: ["<QTable>::<Field>"],
    description:
      "OPTIONAL: Specifies which elements to exclude from the operation.",
  },
  HELP: {
    params: [],
    description:
      "Lists all available commands, or shows detailed help for a specific command.",
  },
}

export const commandCache: CommandCache = {
  list: null,
  details: {},
  pendingRequests: {},
}

const cacheCommandDetails = (
  command: string,
  details: CommandDefinition
): CommandDefinition => {
  commandCache.details[command] = details
  return details
}

const fallbackFor = (command: string): CommandDefinition =>
  fallbackCommands[command] || {
    params: [],
    description: "Command information not available",
  }

// Listeners notified whenever a HELP <command> response lands in the cache,
// so an open suggestion popup can re-render progressively instead of waiting
// for every request up front.
const detailsListeners = new Set<() => void>()

export const subscribeToCommandDetails = (listener: () => void): (() => void) => {
  detailsListeners.add(listener)
  return () => {
    detailsListeners.delete(listener)
  }
}

const notifyDetailsListeners = (): void => {
  detailsListeners.forEach((listener) => listener())
}

export const applyCachedDetails = (items: SuggestionItem[]): SuggestionItem[] =>
  items.map((item) => {
    if (!item.loading) return item
    const details = commandCache.details[item.title]
    if (!details) return item
    return { title: item.title, ...details, loading: false }
  })

/** Purpose-driven sections for the `/` popup (same idea as the Transform
 *  "Add Step" picker's STEP_GROUPS) — scan-to-the-right-pattern, not
 *  alphabetical. Commands the engine adds later fall into "Other". */
const COMMAND_GROUPS: ReadonlyArray<{ label: string; commands: string[] }> = [
  {
    label: "Inspect",
    commands: ["PDUMP", "INFO", "CODEBOOK", "FIND", "FINDR", "HELP"],
  },
  {
    label: "Output",
    commands: ["OUTPUT", "OUTPUTJ", "DUMP", "SDUMP"],
  },
  {
    label: "Tables",
    commands: ["XTAB", "DTAB"],
  },
  {
    label: "Structure",
    commands: [
      "QTABLE",
      "FIELD",
      "FFIELD",
      "PROJECT",
      "APPEND",
      "APPENDSTRUCT",
      "COPY",
      "COPYSTRUCT",
      "RENAME",
      "ALIAS",
      "REMOVE",
    ],
  },
  {
    label: "Transform",
    commands: [
      "ASSIGN",
      "CALC",
      "CAT",
      "ADD",
      "SUBTRACT",
      "MULT",
      "DIV",
      "AND",
      "OR",
      "NOT",
      "BLANKTRUE",
      "BLANKFALSE",
      "BLANKZERO",
      "SETBLANK",
    ],
  },
  {
    label: "Weighting",
    commands: ["USEWEIGHTS", "CALCWEIGHTS"],
  },
  {
    label: "Session",
    commands: [
      "SCOPE",
      "EXCL",
      "RESPID",
      "REMEMBER",
      "RECALL",
      "FORGET",
      "RESET",
      "CLEAR",
      "STOP",
    ],
  },
]

const OTHER_GROUP_LABEL = "Other"

// command -> { group index, position within group } for ordering lookups
const commandGroupOrder = new Map<string, { group: number; pos: number }>()
COMMAND_GROUPS.forEach((group, groupIndex) => {
  group.commands.forEach((command, pos) => {
    commandGroupOrder.set(command, { group: groupIndex, pos })
  })
})

const commandGroupLabel = (command: string): string => {
  const order = commandGroupOrder.get(command)
  return order ? COMMAND_GROUPS[order.group].label : OTHER_GROUP_LABEL
}

export const sortCommands = (commands: string[]): string[] =>
  [...commands].sort((a, b) => {
    const orderA = commandGroupOrder.get(a)
    const orderB = commandGroupOrder.get(b)
    if (!orderA && !orderB) return a.localeCompare(b)
    if (!orderA) return 1
    if (!orderB) return -1
    return orderA.group - orderB.group || orderA.pos - orderB.pos
  })

const escapeHtml = (str: string): string => {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;")
}

const parseHelpOutput = (helpOutput: EngineQueryResult["result"]): string[] => {
  try {
    if (helpOutput?.output && Array.isArray(helpOutput.output)) {
      const helpItem = helpOutput.output.find(
        (item: EngineOutputItem) => item.type === "dump"
      )

      if (helpItem?.content) {
        let helpText: string | undefined

        // Handle different content types from your union
        if (typeof helpItem.content === "string") {
          helpText = helpItem.content
        } else if (helpItem.content && typeof helpItem.content === "object") {
          // Check if it's EngineTableDumpContent or GenericJsonDumpContent with string value
          if ("value" in helpItem.content) {
            if (typeof helpItem.content.value === "string") {
              helpText = helpItem.content.value
            }
          }
        }

        if (helpText && typeof helpText === "string") {
          return helpText
            .split("\n")
            .slice(1)
            .map((line: string) => line.trim())
            .filter(Boolean)
        }
      }
    }
    return Object.keys(fallbackCommands)
  } catch (error) {
    console.warn("Error parsing HELP output:", error)
    return Object.keys(fallbackCommands)
  }
}

export const parseCommandHelp = (
  helpOutput: EngineQueryResult["result"],
  command: string
): CommandDefinition => {
  try {
    if (helpOutput?.output && Array.isArray(helpOutput.output)) {
      const helpItem = helpOutput.output.find(
        (item: EngineOutputItem) => item.type === "dump"
      )

      let helpContent: string | undefined

      if (helpItem?.content) {
        // Handle different content types from your union
        if (typeof helpItem.content === "string") {
          helpContent = helpItem.content
        } else if (helpItem.content && typeof helpItem.content === "object") {
          // Check if it's EngineTableDumpContent or GenericJsonDumpContent with string value
          if ("value" in helpItem.content) {
            if (typeof helpItem.content.value === "string") {
              helpContent = helpItem.content.value
            }
          }
        }
      }

      // No help section for this command (the engine answers "No help
      // available for the command: X") — cache the fallback so the popup
      // does not refetch it on every keystroke.
      if (!helpContent || helpContent.includes("No help available")) {
        return cacheCommandDetails(command, fallbackFor(command))
      }

      // Engine help sections (see DataQ_Engine/DataQ_Query/Query_Help.cpp)
      // start with a description sentence, followed by "Parameters:" and
      // "Supported Types:" blocks, each terminated by a blank line:
      //   Creates new data field within specified QTable. ...
      //
      //   Parameters:
      //   <QTable>::<Field> - Full path to new field
      //   <Value> - (Optional) Initial value for the field, ...
      //
      //   Supported Types:
      //   BOOL - Boolean values (true/false)   (or a comma list: "INT, REAL")
      const lines = helpContent
        .split(/\r?\n/)
        .map((line: string) => line.trim())

      const sectionLines = (heading: string): string[] => {
        const start = lines.indexOf(heading)
        if (start === -1) return []
        const body: string[] = []
        for (let i = start + 1; i < lines.length && lines[i] !== ""; i++) {
          body.push(lines[i])
        }
        return body
      }

      const description = lines.find(Boolean) || ""

      const params = sectionLines("Parameters:")
        .filter((line) => !/^none$/i.test(line))
        .filter((line) => !line.split(" - ")[1]?.trimStart().startsWith("(Optional)"))
        .map((line) => line.split(" - ")[0].trim())
        .filter(Boolean)

      const types = sectionLines("Supported Types:").flatMap((line) =>
        line.includes(" - ")
          ? [line.split(" - ")[0].trim()]
          : line
              .split(",")
              .map((t) => t.trim())
              .filter(Boolean)
      )

      return cacheCommandDetails(command, {
        params,
        description,
        types: types.length > 0 ? types : undefined,
      })
    }

    // No output or invalid format — cache the fallback.
    return cacheCommandDetails(command, fallbackFor(command))
  } catch (error) {
    console.warn("Error parsing command help:", error)
    return cacheCommandDetails(command, fallbackFor(command))
  }
}

export const renderItems = (
  items: SuggestionItem[],
  selectedIndex: number
): string => {
  let previousGroup: string | null = null
  return `
    <div class="suggestions">
      ${items
        .map((item, index) => {
          const group = commandGroupLabel(item.title)
          const header =
            group === previousGroup
              ? ""
              : `<div class="suggestion-group-label">${escapeHtml(
                  group
                )}</div>`
          previousGroup = group
          return `
        ${header}
        <div class="suggestion-item ${
          index === selectedIndex ? "selected" : ""
        }" data-index="${index}">
          <div class="suggestion-title">
            ${escapeHtml(item.title)}
            ${
              item.params?.length > 0
                ? `<span class="suggestion-params">${item.params
                    .map((param) => escapeHtml(param))
                    .join(" ")}</span>`
                : ""
            }
          </div>
          <div class="suggestion-description">
            ${item.loading ? "Loading..." : escapeHtml(item.description || "")}
            ${
              item.types
                ? `<div class="suggestion-types">Types: ${item.types.join(
                    ", "
                  )}</div>`
                : ""
            }
          </div>
        </div>
      `
        })
        .join("")}
    </div>
  `
}

const fetchCommandDetails = (
  command: string,
  RunEngineQuery: Window["RunEngineQuery"],
  queryInstance: NonNullable<Window["queryInstance"]>
): Promise<CommandDefinition> => {
  if (commandCache.details[command]) {
    return Promise.resolve(commandCache.details[command])
  }

  if (command in commandCache.pendingRequests) {
    return commandCache.pendingRequests[command]
  }

  const requestPromise = (async (): Promise<CommandDefinition> => {
    try {
      const response = await RunEngineQuery(
        queryInstance,
        `HELP ${command}`,
        false
      )
      const engineResult = response?.data as EngineQueryResult
      if (engineResult?.result?.output) {
        return parseCommandHelp(engineResult.result, command)
      }
      throw new Error(`Invalid HELP response for ${command}`)
    } catch (err) {
      console.warn(`Failed to fetch details for ${command}, using fallback:`, err)
      return cacheCommandDetails(
        command,
        fallbackCommands[command] || {
          params: [],
          description: "Failed to load details",
        }
      )
    } finally {
      delete commandCache.pendingRequests[command]
    }
  })()

  commandCache.pendingRequests[command] = requestPromise
  requestPromise.then(notifyDetailsListeners)
  return requestPromise
}

export const getSuggestionItems = async ({
  query,
}: SuggestionItemsProps): Promise<SuggestionItem[]> => {
  const RunEngineQuery = window.RunEngineQuery
  const queryInstance = window.queryInstance

  if (!RunEngineQuery || !queryInstance) {
    const commands = Object.keys(fallbackCommands)
    const filtered = sortCommands(
      commands.filter((command: string) =>
        command.toLowerCase().startsWith(query.toLowerCase())
      )
    )
    return filtered.map(
      (command: string): SuggestionItem => ({
        title: command,
        ...fallbackCommands[command],
        loading: false,
      })
    )
  }

  if (!commandCache.list) {
    try {
      const response = await RunEngineQuery(queryInstance, "HELP", false)
      const engineResult = response?.data as EngineQueryResult
      if (engineResult?.result?.output) {
        commandCache.list = parseHelpOutput(engineResult.result)
      } else {
        throw new Error("Invalid HELP response format")
      }
    } catch (err) {
      console.warn("Failed to fetch command list:", err)
      commandCache.list = Object.keys(fallbackCommands)
    }
  }

  const filteredCommands = sortCommands(
    commandCache.list.filter((command: string) =>
      command.toLowerCase().startsWith(query.toLowerCase())
    )
  )

  const items: SuggestionItem[] = filteredCommands.map(
    (command: string): SuggestionItem => ({
      title: command,
      loading: !commandCache.details[command],
      description: commandCache.details[command]?.description || "Loading...",
      params: commandCache.details[command]?.params || [],
      types: commandCache.details[command]?.types,
    })
  )

  // Fire detail fetches in the background — the popup renders immediately
  // with cached/loading entries and re-renders via subscribeToCommandDetails
  // as responses land.
  filteredCommands.forEach((command: string) => {
    void fetchCommandDetails(command, RunEngineQuery, queryInstance)
  })

  return items
}

export const CommandSuggestion = Extension.create({
  name: "commandSuggestion",

  addOptions() {
    return {
      suggestion: {
        char: "/",
        startOfLine: false,
        command: ({ editor, range, props }: SuggestionCommandProps) => {
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .insertContent(props.title + " ")
            .run()

          return true
        },
        items: getSuggestionItems,
        render: () => {
          let popup: TippyInstance[]
          let element: HTMLDivElement
          let selectedIndex = 0
          let items: SuggestionItem[] = []
          let editorInstance: unknown
          let rangeInstance: Range
          let unsubscribeDetails: (() => void) | null = null

          const selectItem = (index: number): void => {
            selectedIndex = index
            element.innerHTML = renderItems(items, selectedIndex)
            attachClickHandlers()
            const selectedItem = element.querySelector(
              ".suggestion-item.selected"
            ) as HTMLElement
            if (selectedItem) {
              selectedItem.scrollIntoView({
                block: "nearest",
                inline: "nearest",
              })
            }
          }

          const insertSelectedItem = (): void => {
            const item = items[selectedIndex]
            if (item && editorInstance && rangeInstance) {
              popup[0].hide()
              setTimeout(() => {
                // Drop bracketed type-enumeration params like
                // "[INT|REAL|TEXT|BOOL]" (e.g. from HELP FIELD) — they're a
                // hint about allowed types, not a literal placeholder the user
                // should keep. Plain "[search_string]" placeholders (no pipe)
                // are preserved.
                const insertableParams = (item.params ?? []).filter(
                  (param) => !/^\[.*\|.*\]$/.test(param)
                )
                const paramsString =
                  insertableParams.length > 0
                    ? " " + insertableParams.join(" ")
                    : ""
                ;(editorInstance as Editor)
                  .chain()
                  .focus()
                  .deleteRange(rangeInstance)
                  .insertContent(item.title + paramsString + " ")
                  .run()
              }, 0)
            }
          }

          const attachClickHandlers = (): void => {
            const suggestionItems = element.querySelectorAll(".suggestion-item")
            suggestionItems.forEach((el) => {
              el.addEventListener("click", () => {
                const index = Number(el.getAttribute("data-index"))
                selectedIndex = index
                insertSelectedItem()
              })
            })
          }

          return {
            onStart: (props: SuggestionProps) => {
              editorInstance = props.editor
              rangeInstance = props.range

              element = document.createElement("div")
              items = props.items
              element.innerHTML = renderItems(items, selectedIndex)

              popup = tippy("body", {
                getReferenceClientRect: props.clientRect || undefined,
                appendTo: () => document.body,
                content: element,
                showOnCreate: true,
                interactive: true,
                trigger: "manual",
                placement: "bottom-start",
                arrow: true,
                theme: "dataq",
              })

              attachClickHandlers()

              // Re-render in place as HELP <command> responses land. Items
              // are patched from the cache; identity comparison skips the
              // re-render when nothing this popup shows has changed.
              unsubscribeDetails = subscribeToCommandDetails(() => {
                const refreshed = applyCachedDetails(items)
                if (refreshed.some((item, index) => item !== items[index])) {
                  items = refreshed
                  element.innerHTML = renderItems(items, selectedIndex)
                  attachClickHandlers()
                }
              })
            },
            onUpdate(props: SuggestionProps) {
              items = props.items
              element.innerHTML = renderItems(items, selectedIndex)
              rangeInstance = props.range
              attachClickHandlers()
            },
            onKeyDown(props: KeyboardEventProps): boolean {
              const { event } = props

              if (event.key === "Escape") {
                popup[0].hide()
                return true
              }

              if (event.key === "ArrowUp") {
                event.preventDefault()
                selectItem(Math.max(selectedIndex - 1, 0))
                return true
              }

              if (event.key === "ArrowDown") {
                event.preventDefault()
                selectItem(Math.min(selectedIndex + 1, items.length - 1))
                return true
              }

              if (event.key === "Enter" || event.key === "Tab") {
                event.preventDefault()
                insertSelectedItem()
                return true
              }

              return false
            },
            onExit() {
              unsubscribeDetails?.()
              unsubscribeDetails = null
              popup[0].destroy()
            },
          }
        },
      },
    }
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ]
  },
})

const suggestionStyles = `
.tippy-box[data-theme~='dataq'] {
  background-color: #ffffff;
  color: #333333;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.tippy-box[data-theme~='dataq'] .tippy-content {
  padding: 0;
}

.tippy-box[data-theme~='dataq'] .tippy-arrow {
  display: none;
}

.suggestions {
  padding: 0.375rem;
  max-height: 250px;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: #ffffff;
  border: 0;
  border-radius: 8px;
  scrollbar-width: thin;
  scrollbar-color: rgba(0,0,0,0.3) transparent;
}

.suggestion-group-label {
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #9a9a9a;
  padding: 0.5rem 0.5rem 0.2rem;
}

.suggestion-group-label:first-child {
  padding-top: 0.25rem;
}

.suggestions::-webkit-scrollbar {
  width: 4px;
}

.suggestions::-webkit-scrollbar-track {
  background: transparent;
}

.suggestions::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 2px;
}

.suggestions::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.5);
}

.suggestions::-webkit-scrollbar-corner {
  background: transparent;
}

.suggestion-item {
  padding: 0.5rem;
  margin: 0.1rem 0;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  background-color: #ffffff;
  font-size: 0.85rem;
  line-height: 1.2;
  color: #333333;
}

.suggestion-item:hover {
  background-color: #f5f5f5;
  border-color: #cccccc;
}

.suggestion-item.selected {
  background-color: #e9f7fd;
  border-color: #bcdff5;
}

.suggestion-title {
  font-weight: 600;
  color: #36c3ed;
  font-size: 0.85rem;
  margin-bottom: 0.15rem;
}

.suggestion-params {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  color: #666666;
  background-color: rgba(0, 0, 0, 0.045);
  padding: 0.15rem 0.4rem;
  border-radius: 4px;
  margin-left: 0.3rem;
  font-size: 0.75rem;
  display: inline-block;
  white-space: nowrap;
}

.suggestion-description {
  font-size: 0.75rem;
  color: #666666;
  line-height: 1.2;
}

.suggestion-types {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.7rem;
  color: #666;
  margin-top: 0.25rem;
  padding-top: 0.25rem;
  border-top: 1px solid #eee;
}
`

const style = document.createElement("style")
style.textContent = suggestionStyles
document.head.appendChild(style)
